/* eslint-disable no-console */
import PropTypes from "prop-types";
import React, { useState } from "react";
import Card from "@mui/material/Card";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { useTranslation } from "react-i18next";
import DataTable from "components/DataTable";
import { connect } from "react-redux";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import { useMaterialUIController } from "context";

import { Modal } from "@mui/material";

import { sendRemoveProjectManager } from "store/actions/projectManagersActions";
import { newAlertMessage } from "store/actions/globalAlertActions";
import ProjectManagerForm from "./ProjectManagerForm";

const getProjectNames = (projectsValue = {}, projectsReducer = {}) => {
  const projectIds = Object.keys(projectsValue);

  if (projectIds.length === 0) return "-";

  return projectIds
    .map((projectId) => projectsReducer?.[projectId]?.name || "")
    .filter(Boolean)
    .join(", ");
};

function AlertDialog({ open, handleClose, handleAgree, message, showCancel }) {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const { t } = useTranslation();

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <MDBox
        sx={({ palette: { white, background } }) => ({
          backgroundColor: darkMode ? background.sidenav : white.main,
        })}
      >
        <DialogTitle id="alert-dialog-title">{t("confirmation")}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {message || t("stepNotUndo")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          {showCancel && <MDButton onClick={handleClose}>{t("cancel")}</MDButton>}
          <MDButton onClick={handleAgree} autoFocus>
            {t("OK")}
          </MDButton>
        </DialogActions>
      </MDBox>
    </Dialog>
  );
}

function ProjectManagers({
  projectManagers,
  projectsReducer,
  actRemoveProjectManager,
  actNewAlertMessage,
}) {
  const { t } = useTranslation();
  const [actionType, setActionType] = useState("");
  const [selectedProjectManagerUid, setSelectedProjectManagerUid] = useState("");
  const [pendingRemoveUid, setPendingRemoveUid] = useState("");
  const [openErrorDialog, setOpenErrorDialog] = useState(false);
  const [errorDialogMessage, setErrorDialogMessage] = useState("");

  const [open, setOpen] = useState(false);
  const [openConfirm, setOpenConfirm] = useState(false);

  const add = () => {
    setActionType("add");
    setSelectedProjectManagerUid("");
    setOpen(true);
  };

  const edit = (selectedRows) => {
    const selectedUid = selectedRows?.[0]?.original?.uid;
    if (!selectedUid) return;

    setActionType("edit");
    setSelectedProjectManagerUid(selectedUid);
    setOpen(true);
  };

  const remove = (selectedRows) => {
    const selectedUid = selectedRows?.[0]?.original?.uid;
    if (!selectedUid) return;

    setPendingRemoveUid(selectedUid);
    setOpenConfirm(true);
  };

  const confirmRemove = async () => {
    if (!pendingRemoveUid) {
      setOpenConfirm(false);
      return;
    }

    try {
      await actRemoveProjectManager(pendingRemoveUid);
      actNewAlertMessage({ message: t("Success"), severity: "success" });
    } catch (error) {
      console.error("Error removing project manager:", error);
      actNewAlertMessage({ message: t("unknownError"), severity: "error" });
    } finally {
      setPendingRemoveUid("");
      setOpenConfirm(false);
    }
  };

  const columns = () => [
    { Header: t("name"), accessor: "firstName", maxWidth: 20 },
    { Header: t("lastName"), accessor: "lastName", maxWidth: 20 },
    { Header: t("email"), accessor: "email", maxWidth: 20 },
    { Header: t("phoneNumber"), accessor: "phoneNumber", maxWidth: 20 },
    {
      Header: t("projects"),
      accessor: "projects",
      maxWidth: 40,
      Cell: ({ cell: { value } }) => getProjectNames(value, projectsReducer),
    },
  ];

  return (
    <Card id="projectManagers" sx={{ overflow: "visible" }}>
      <AlertDialog
        open={openConfirm}
        handleClose={() => {
          setOpenConfirm(false);
          setPendingRemoveUid("");
        }}
        handleAgree={confirmRemove}
        showCancel
      />

      <AlertDialog
        open={openErrorDialog}
        handleClose={() => {
          setOpenErrorDialog(false);
          setErrorDialogMessage("");
        }}
        handleAgree={() => {
          setOpenErrorDialog(false);
          setErrorDialogMessage("");
        }}
        message={errorDialogMessage}
        showCancel={false}
      />

      <Modal open={open} onClose={() => setOpen(false)} style={{ overflow: "scroll" }}>
        <MDBox
          mt={3}
          sx={{
            width: { xs: "90vw", md: "65vw" },
            position: "absolute",
            left: "50%",
            transform: "translateX(-50%)",
          }}
        >
          <ProjectManagerForm
            actionType={actionType}
            projectManagerUid={selectedProjectManagerUid}
            onSuccessClose={() => {
              setOpen(false);
              setSelectedProjectManagerUid("");
            }}
          />
        </MDBox>
      </Modal>

      <MDBox p={3}>
        <MDTypography variant="h5">{t("Project Managers")}</MDTypography>

        <DataTable
          table={{ columns: columns(), rows: Object.values(projectManagers || {}) }}
          canSearch
          canSelect
          canEdit
          canAdd
          canActuate
          action={() => {}}
          actionIcon="arrow_forward_ios"
          actionColor="info"
          add={add}
          edit={edit}
          remove={remove}
        />
      </MDBox>
    </Card>
  );
}

ProjectManagers.propTypes = {
  projectManagers: PropTypes.object,
  projectsReducer: PropTypes.object,
  actRemoveProjectManager: PropTypes.func.isRequired,
  actNewAlertMessage: PropTypes.func.isRequired,
};

ProjectManagers.defaultProps = {
  projectManagers: {},
  projectsReducer: {},
};

AlertDialog.defaultProps = {
  handleAgree: () => {},
  message: "",
  showCancel: true,
};

AlertDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  handleAgree: PropTypes.func,
  message: PropTypes.string,
  showCancel: PropTypes.bool,
};

const mapStateToProps = (state) => {
  const projectManagers = state?.projectManagers;
  const projectsReducer = state?.projectsReducer;
  return { projectManagers, projectsReducer };
};

const mapDispatchToProps = (dispatch) => ({
  actRemoveProjectManager: (projectManagerUid) =>
    new Promise((resolve, reject) => {
      dispatch(sendRemoveProjectManager(projectManagerUid, resolve, reject));
    }),
  actNewAlertMessage: (message) => dispatch(newAlertMessage(message)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ProjectManagers);
